import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import { AuthService } from '@services/core/auth.service';
import { SocketService, AccessChangedEvent } from '@services/core/socket.service';
import { CatalogService } from '@services/data/catalog.service';
import { RequestService, Request } from '../../../feature/management-request/services/requests.service';
import { BpmService } from '../../../feature/management-request/services/bpm.service';
import { PeriodService } from '../../../feature/management/billing-management/services/period.service';

export interface AppNotification {
  key: string;
  title: string;
  message: string;
  uuid?: string;
  type: string;
  at: Date;
  read: boolean;
}

const MAX_ITEMS = 20;
const MAX_OPENED = 200;
const SKIP_TYPES = new Set(['WELCOME', 'ACCESS_CHANGED']);
const PERIOD_TYPE = 'PERIOD_UPDATED';
const ACCESS_REASONS: Record<string, string> = {
  MENU_UPDATED: 'Se actualizó el menú del sistema',
  ROLE_MENU_UPDATED: 'Se actualizaron los permisos de su rol',
  ROLE_UPDATED: 'Se actualizó su rol',
  ENTITY_UPDATED: 'Se actualizó una entidad vinculada',
  USER_UPDATED: 'Se actualizó su usuario o entidades'
};

@Injectable({ providedIn: 'root' })
export class NotificationHubService {
  private socketService = inject(SocketService);
  private authService = inject(AuthService);
  private catalogService = inject(CatalogService);
  private requestService = inject(RequestService);
  private bpmService = inject(BpmService);
  private periodService = inject(PeriodService);
  private router = inject(Router);

  readonly items = signal<AppNotification[]>([]);
  readonly unreadCount = computed(() => this.items().filter(n => !n.read).length);

  /**
   * Pendientes ya abiertos por este usuario (uuid::statusSlug).
   * Si el BPM cambia de estado, la clave cambia y vuelve a verse en la campana.
   */
  readonly openedKeys = signal<ReadonlySet<string>>(new Set());

  private readonly soundUrl = 'https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3';
  private accessTimer: ReturnType<typeof setTimeout> | null = null;
  private accessBound = false;
  private openedStorageUserId: string | null = null;

  constructor() {
    this.bindSocket();
    this.bindAccessChanged();
    this.bindCatalogChanged();
    this.joinRoomsOnAuth();
    this.bindOpenedStorage();
  }

  /** True si este usuario ya abrió la solicitud en el estado BPM actual. */
  isOpened(request: Request | null | undefined): boolean {
    const key = this.openedKeyFor(request);
    return !!key && this.openedKeys().has(key);
  }

  /** Marca la solicitud como abierta para el estado actual (deja de salir en pendientes). */
  markOpened(request: Request | null | undefined): void {
    const key = this.openedKeyFor(request);
    if (!key) return;
    if (this.openedKeys().has(key)) return;
    const next = new Set(this.openedKeys());
    next.add(key);
    while (next.size > MAX_OPENED) {
      const first = next.values().next().value;
      if (first == null) break;
      next.delete(first);
    }
    this.openedKeys.set(next);
    this.persistOpened(next);
    if (request?.uuid) {
      this.items.update((list) =>
        list.map((n) => (n.uuid === request.uuid ? { ...n, read: true } : n))
      );
    }
  }

  markAllRead(): void {
    this.items.update(list => list.map(n => ({ ...n, read: true })));
  }

  open(notice: AppNotification): void {
    this.items.update(list => list.map(n => n.key === notice.key ? { ...n, read: true } : n));
    if (notice.uuid) {
      void this.openRequest(notice.uuid);
    }
  }

  /**
   * Abre una solicitud en la bandeja.
   * Si viene targetRole (campana), cambia a ese rol si no es el activo; si no, lo resuelve por BPM.
   */
  async openRequest(
    uuid: string,
    request?: Request | null,
    targetRole?: { id?: number; name?: string } | null
  ): Promise<void> {
    const id = String(uuid || '').trim();
    if (!id) return;

    let req =
      request ||
      this.requestService.requests().find((r) => r.uuid === id || String((r as any).id) === id) ||
      null;

    if (targetRole?.id) {
      await this.switchToRole(targetRole);
    } else {
      await this.ensureRoleForRequest(req);
    }

    req =
      this.requestService.requests().find((r) => r.uuid === id || String((r as any).id) === id) ||
      req;

    const activeName = (this.authService.sharingRole() as any)?.slug ?? (this.authService.sharingRole() as any)?.name;
    if (req && !this.bpmService.canActOnRequest(req, activeName, this.userMail())) {
      await this.ensureRoleForRequest(req);
    }

    this.markOpened(req ?? ({ uuid: id, statusSlug: '' } as Request));
    this.requestService.focusRequest(id);
    await this.router.navigate(['/solicitudes', id]);
  }

  private openedKeyFor(request: Request | null | undefined): string | null {
    const uuid = String(request?.uuid || '').trim();
    if (!uuid) return null;
    const status = String(request?.statusSlug || '').trim().toUpperCase();
    return `${uuid}::${status}`;
  }

  private openedStorageKey(userId: string | number): string {
    return `fpv.inbox.opened.${userId}`;
  }

  private bindOpenedStorage(): void {
    effect(() => {
      const identity = this.authService.sharingIdentity() as any;
      const userId = identity?.id ?? (this.authService.userData() as any)?.id ?? null;
      if (userId == null) {
        this.openedKeys.set(new Set());
        this.openedStorageUserId = null;
        return;
      }
      const id = String(userId);
      if (this.openedStorageUserId === id) return;
      this.openedStorageUserId = id;
      this.openedKeys.set(this.loadOpened(id));
    });
  }

  private loadOpened(userId: string): Set<string> {
    try {
      const raw = localStorage.getItem(this.openedStorageKey(userId));
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set();
      return new Set(parsed.map((x) => String(x)).filter(Boolean).slice(-MAX_OPENED));
    } catch {
      return new Set();
    }
  }

  private persistOpened(keys: ReadonlySet<string>): void {
    const userId = this.openedStorageUserId;
    if (!userId) return;
    try {
      localStorage.setItem(this.openedStorageKey(userId), JSON.stringify([...keys]));
    } catch {
      // quota / private mode
    }
  }

  private userMail(): string | null {
    const identity = this.authService.sharingIdentity() as any;
    const data = this.authService.userData() as any;
    return String(identity?.mail ?? data?.mail ?? '').trim().toLowerCase() || null;
  }

  private async switchToRole(role: { id?: number; name?: string }): Promise<void> {
    if (!role?.id) return;
    const identityRoleId = Number((this.authService.sharingIdentity() as any)?.roleId);
    const current = this.authService.sharingRole() as any;
    if (Number(current?.id) === Number(role.id) && identityRoleId === Number(role.id)) return;

    try {
      await firstValueFrom(this.authService.changeRole(Number(role.id)));
      // changeRole ya sincroniza sharingRole / identity desde la respuesta API
      await this.requestService.reloadRequests();
    } catch (err) {
      console.error('[notif] no se pudo cambiar al rol', role?.name, err);
    }
  }

  private async ensureRoleForRequest(request: Request | null): Promise<void> {
    if (!request) return;
    const roles = (this.authService.sharingRoles() as any[]) || [];
    const current = this.authService.sharingRole() as any;
    const target = this.bpmService.pickRoleForRequest(
      request,
      roles,
      current?.slug ?? current?.name,
      this.userMail()
    );
    if (!target?.id) return;
    await this.switchToRole(target);
  }

  private joinRoomsOnAuth(): void {
    effect(() => {
      const identity = this.authService.sharingIdentity() as any;
      const role = this.authService.sharingRole() as any;
      const activeEntity = this.authService.sharingEntity() as any;
      const activeEntities = (this.authService.sharingEntities() as any[]) || [];
      const byRole = this.authService.sharingEntitiesByRole() || {};

      const seen = new Set<string>();
      const entityCodes: Array<string | number> = [];
      const addCode = (code: string | number | null | undefined) => {
        if (code == null || code === '') return;
        const key = String(code);
        if (seen.has(key)) return;
        seen.add(key);
        entityCodes.push(code);
        this.socketService.join(code);
      };

      Object.values(byRole).forEach((list) => {
        (Array.isArray(list) ? list : []).forEach((e: any) => addCode(e?.code));
      });
      activeEntities.forEach((e) => addCode(e?.code));
      addCode(activeEntity?.code);

      if (!entityCodes.length && !identity?.id) return;

      this.socketService.joinAccess({
        userId: identity?.id ?? null,
        roleId: role?.id ?? identity?.roleId ?? null,
        entityCodes
      });
    });
  }

  private bindSocket(): void {
    this.socketService.onNotification().subscribe(notice => {
      const type = String(notice?.type || 'INFO');
      if (SKIP_TYPES.has(type)) return;

      const uuid = notice?.data?.uuid || notice?.uuid || null;
      const item: AppNotification = {
        key: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        title: notice?.title || 'Aviso de sistema',
        message: notice?.message || 'Hay una actualización en solicitudes',
        uuid: uuid || undefined,
        type,
        at: notice?.timestamp ? new Date(notice.timestamp) : new Date(),
        read: false
      };

      this.items.update(list => [item, ...list].slice(0, MAX_ITEMS));
      if (type === PERIOD_TYPE) {
        this.periodService.reload();
      } else {
        this.requestService.loadRequests();
      }
      this.playSound();
      this.showToast(item);
    });
  }

  private bindAccessChanged(): void {
    if (this.accessBound) return;
    this.accessBound = true;
    this.socketService.onAccessChanged().subscribe(ev => this.onAccessChanged(ev));
  }

  private bindCatalogChanged(): void {
    this.socketService.onCatalogChanged().subscribe((ev) => {
      this.catalogService.applyCatalogChanged(ev?.scopes);
    });
  }

  private onAccessChanged(ev: AccessChangedEvent): void {
    if (ev?.forceLogout) {
      this.authService.sessionExpired$.next(true);
      return;
    }
    if (this.accessTimer) clearTimeout(this.accessTimer);
    this.accessTimer = setTimeout(() => this.refreshAccessQuiet(ev), 150);
  }

  // Refresca sesion y recarga solicitudes tras confirmacion de socket
  private async refreshAccessQuiet(ev: AccessChangedEvent): Promise<void> {
    const ok = await this.authService.refreshSessionAccess();
    if (!ok) return;
    this.requestService.loadRequests();
    const reason = String(ev?.reason ?? '');
    const message = ACCESS_REASONS[reason] ?? 'Se actualizó su sesión de acceso';
    this._showSessionToast(message);
  }

  private _showSessionToast(message: string): void {
    Swal.fire({
      title: 'Sesión actualizada',
      html: `<span style="font-size: 0.9em;">${message}</span>`,
      icon: 'info',
      timer: 1500,
      timerProgressBar: true,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      background: 'var(--bs-component-bg)',
      color: 'var(--bs-heading-color)'
    });
  }

  private showToast(notice: AppNotification): void {
    Swal.fire({
      title: `<span style="font-weight: 600;">${notice.title}</span>`,
      html: `<span style="font-size: 0.9em;">${notice.message}</span>`,
      icon: 'info',
      timer: 6000,
      timerProgressBar: true,
      toast: true,
      position: 'top-end',
      showConfirmButton: !!notice.uuid,
      confirmButtonText: 'Ver',
      background: 'var(--bs-component-bg)',
      color: 'var(--bs-heading-color)',
      customClass: { popup: 'premium-toast' }
    }).then(result => {
      if (result.isConfirmed && notice.uuid) {
        this.open(notice);
      }
    });
  }

  private playSound(): void {
    const audio = new Audio(this.soundUrl);
    audio.volume = 0.5;
    audio.play().catch(() => undefined);
  }
}
